import React, { useCallback, useState, useMemo } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { useArrowStore } from '@/stores/arrowStore';
import { Home, Folder } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TextFormattingSidebar } from '@/components/sidebar/TextFormattingSidebar';
import { ArrowFormattingSidebar } from '@/components/sidebar/ArrowFormattingSidebar';
import { BoardsFanCard } from './BoardsFanCard';

export function FloatingHomeButton() {
  const { currentBoardId, goToFocusLanding, viewMode, activeEditor, isEditingItem, boards } = useCanvasStore();
  const { selectedArrowId } = useArrowStore();
  const [showFanCard, setShowFanCard] = useState(false);

  // Check if we're at the home board
  const isAtHome = useMemo(() => {
    const homeBoard = boards.find(b => b.parent_id === null);
    return homeBoard && currentBoardId === homeBoard.id;
  }, [boards, currentBoardId]);

  const handleHomeClick = useCallback(() => {
    goToFocusLanding();
  }, [goToFocusLanding]);

  // Only show when in Focus mode with a board selected
  if (viewMode !== 'focus' || !currentBoardId) return null;

  const showTextSidebar = isEditingItem && activeEditor;
  const showArrowSidebar = selectedArrowId !== null;
  const showSidebar = showTextSidebar || showArrowSidebar;

  const buttonBase = cn(
    'p-2.5 rounded-xl shadow-lg border transition-all duration-200',
    'hover:scale-105 active:scale-95'
  );

  return (
    <>
      {/* Home and Boards buttons - nudge right when sidebar is open */}
      <div 
        className={cn(
          "fixed top-[4.5rem] z-40 flex flex-col gap-2 transition-all duration-300",
          showSidebar ? "left-[6rem]" : "left-4"
        )}
      >
        {/* Home button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleHomeClick}
              className={cn(
                buttonBase,
                isAtHome
                  ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                  : 'bg-background/95 backdrop-blur-sm text-muted-foreground border-border hover:bg-muted hover:text-foreground'
              )}
              title="Create Home"
            >
              <Home className="h-5 w-5" strokeWidth={2} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>Create Home</p>
          </TooltipContent>
        </Tooltip>

        {/* Boards fan card button - directly under home */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setShowFanCard(!showFanCard)}
              className={cn(
                buttonBase,
                showFanCard
                  ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                  : 'bg-background/95 backdrop-blur-sm text-muted-foreground border-border hover:bg-muted hover:text-foreground'
              )}
              title="Recent boards"
            >
              <Folder className="h-5 w-5" strokeWidth={2} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>Recent boards</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Boards Fan Card - positioned next to the button stack */}
      {showFanCard && (
        <>
          {/* Invisible backdrop to close on click outside */}
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setShowFanCard(false)} 
          />
          <div 
            className={cn(
              "fixed top-[4.5rem] z-40 transition-all duration-300",
              showSidebar ? "left-[10rem]" : "left-16"
            )}
          >
            <BoardsFanCard onClose={() => setShowFanCard(false)} />
          </div>
        </>
      )}

      {/* Text Formatting Sidebar - slides in from left when editing text */}
      <div 
        className={cn(
          "fixed top-[4.5rem] left-4 z-40 transition-all duration-300",
          "bg-background/95 backdrop-blur-sm rounded-xl shadow-lg border border-border",
          "py-3",
          showTextSidebar 
            ? "opacity-100 translate-x-0" 
            : "opacity-0 -translate-x-full pointer-events-none"
        )}
      >
        <TextFormattingSidebar editor={activeEditor} />
      </div>

      {/* Arrow Formatting Sidebar - slides in from left when arrow is selected */}
      <div 
        className={cn(
          "fixed top-[4.5rem] left-4 z-40 transition-all duration-300",
          "bg-background/95 backdrop-blur-sm rounded-xl shadow-lg border border-border",
          "py-3",
          showArrowSidebar && !showTextSidebar
            ? "opacity-100 translate-x-0" 
            : "opacity-0 -translate-x-full pointer-events-none"
        )}
      >
        <ArrowFormattingSidebar />
      </div>
    </>
  );
}
